import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import useSubjectAPI from "./useSubjectAPI";
import { phone_types, best_days_to_call } from "../enums";

const PhoneNumbersSection = ({ subjectId, form }) => {
  const isCreate = !subjectId;
  const [open, setOpen] = useState(true);
  const purple = "#2E2868";
  const pillBg = "#ebeaf3";

  const { loadSubject, saveSubjectDirect } = useSubjectAPI(subjectId);

  const [phoneList, setPhoneList] = useState([
    {
      id: null,
      phone_number: "",
      phone_type: "",
      best_days_to_call: "",
      best_time_to_call_start: "",
      best_time_to_call_end: "",
      preferred: false,
      sms_opt_in: false,
    },
  ]);

  const [editingList, setEditingList] = useState([]);

  const [originalPhones, setOriginalPhones] = useState({});

  const handleCancel = (index, field) => {
    const original = originalPhones[`${index}_${field}`];
    if (original !== undefined) {
      setPhoneList((prev) => {
        const newList = [...prev];
        newList[index] = { ...newList[index], [field]: original };
        return newList;
      });
    }
    setEditingList((prev) => {
      const newList = [...prev];
      newList[index] = { ...newList[index], [field]: false };
      return newList;
    });
  };
  // ----------------------------------------------

  /* LOAD (EDIT MODE ONLY) */
  useEffect(() => {
    if (isCreate) return;

    loadSubject().then((res) => {
      console.log("DEBUG: Data loaded from API:", res);
      if (!res?.subject_phone_numbers?.length) return;

      const loadedPhones = res.subject_phone_numbers.map((phone) => ({
        id: phone.id,
        phone_number: phone.phone_number || "",
        phone_type: phone.phone_type || "",
        best_days_to_call: phone.best_days_to_call || "",
        best_time_to_call_start: phone.best_time_to_call_start || "",
        best_time_to_call_end: phone.best_time_to_call_end || "",
        preferred: !!phone.preferred,
        sms_opt_in: !!phone.sms_opt_in,
      }));

      setPhoneList(loadedPhones);
      setEditingList(new Array(loadedPhones.length).fill({}));
    });
  }, [isCreate, subjectId]);

 
  useEffect(() => {
    if (!isCreate || !form?.setValue) return;
    const cleanedPhoneList = phoneList.map((p) => ({
      ...p,
      phone_number: p.phone_number || "",
      phone_type: p.phone_type || null,
      best_days_to_call: p.best_days_to_call || null,
      best_time_to_call_start: p.best_time_to_call_start || null,
      best_time_to_call_end: p.best_time_to_call_end || null,
    }));

    form.setValue({
      key: "subject_phone_numbers",
      value: cleanedPhoneList,
    });
  }, [phoneList, isCreate]);

  const handleRemoveField = async (index) => {
    const phoneToRemove = phoneList[index];
    if (!window.confirm("Are you sure you want to delete this phone number?"))
      return;

    const newList = phoneList.filter((_, i) => i !== index);
    const newEditingList = editingList.filter((_, i) => i !== index);

    setPhoneList(newList);
    setEditingList(newEditingList);

    if (!isCreate && phoneToRemove.id) {
      try {
        await saveSubjectDirect(
          { subjects: { id: subjectId } },
          {
            input_delete: [
              { id: phoneToRemove.id, key: "subject_phone_numbers" },
            ],
          },
        );
      } catch (err) {
        console.error("Delete failed", err);
      }
    }
  };

  const handleChange = (index, field, val) => {
    setPhoneList((prev) => {
      const newList = [...prev];
      newList[index] = { ...newList[index], [field]: val };
      return newList;
    });
  };

  const toggleEdit = (index, field) => {
    if (isCreate) return;

    setOriginalPhones((prev) => ({
      ...prev,
      [`${index}_${field}`]: phoneList[index][field],
    }));
    setEditingList((prev) => {
      const newList = [...prev];
      newList[index] = { ...newList[index], [field]: true };
      return newList;
    });
  };

  const saveField = async (index, field) => {
    if (isCreate) return;
    const phoneData = phoneList[index];

    if (
      field === "phone_number" &&
      phoneData.phone_number &&
      !isValidPhoneNumber(phoneData.phone_number)
    ) {
      alert("Please enter a valid phone number");
      return;
    }

    setEditingList((prev) => {
      const newList = [...prev];
      newList[index] = { ...newList[index], [field]: false };
      return newList;
    });

    if (!phoneData.id) {
      const payload = {
        subjects: { id: subjectId },
        subject_phone_numbers: [
          {
            phone_number: phoneData.phone_number,
            phone_type: phoneData.phone_type || "Mobile",
            [field]: phoneData[field],
          },
        ],
      };
      const res = await saveSubjectDirect(payload);
      if (res) {
        const newRecord = res.subject_phone_numbers?.find(
          (p) => p.phone_number === phoneData.phone_number,
        );
        if (newRecord) handleChange(index, "id", newRecord.id);
      }
      return;
    }

    const payload = {
      subjects: { id: subjectId },
      subject_phone_numbers: [
        {
          id: phoneData.id,
          [field]: phoneData[field],
        },
      ],
    };

    const res = await saveSubjectDirect(payload);

    if (!res) {
      alert("Save failed. Reverting...");
      handleCancel(index, field);
    }
  };

  const saveCheckbox = async (index, field, val) => {
    const originalVal = phoneList[index][field];
    handleChange(index, field, val);
    if (isCreate) return;

    const payload = {
      subjects: { id: subjectId },
      subject_phone_numbers: [{ id: phoneList[index].id, [field]: val }],
    };
    const res = await saveSubjectDirect(payload);
    if (!res) {
      handleChange(index, field, originalVal);
    }
  };

  const handleAddField = () => {
    setPhoneList((prev) => [
      ...prev,
      {
        id: null,
        phone_number: "",
        phone_type: "",
        best_days_to_call: "",
        best_time_to_call_start: "",
        best_time_to_call_end: "",
        preferred: false,
        sms_opt_in: false,
      },
    ]);
    setEditingList((prev) => [...prev, {}]);
  };

  const renderInput = (index, label, field, type = "text") => {
    const values = phoneList[index];
    const isEditing = editingList[index]?.[field];
    const viewStyle = {
      backgroundColor: pillBg,
      padding: "0.5rem 70px 0.5rem 0.5rem",
      cursor: "text",
      userSelect: "text",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    };
    const inputStyle = {
      backgroundColor: isEditing ? "#fff" : pillBg,
      border: isEditing ? `2px solid ${purple}` : "0",
      boxShadow: isEditing ? "0 0 5px rgba(46, 40, 104, 0.2)" : "none",
      padding: isEditing
        ? "0.5rem 110px 0.5rem 0.5rem"
        : "0.5rem 2rem 0.5rem 0.5rem",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      cursor: isCreate ? "pointer" : "text",
    };

    return (
      <div className="mb-3">
        <label className="form-label mb-1">{label}</label>
        <div style={{ position: "relative" }}>
          {isCreate || isEditing ? (
            <div className="phone-input-container">
              {field === "phone_number" ? (
                <PhoneInput
                  international
                  defaultCountry="US"
                  value={values[field]}
                  onChange={(val) => handleChange(index, field, val)}
                  className={`form-control rounded-pill ${isEditing ? "" : "border-0"}`}
                  style={inputStyle}
                />
              ) : (
                <input
                  type={type}
                  value={values[field]}
                  onChange={(e) => handleChange(index, field, e.target.value)}
                  className="form-control rounded-pill"
                  style={inputStyle}
                />
              )}
              {isEditing && !isCreate && (
                <div
                  style={{
                    position: "absolute",
                    right: 6,
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 3,
                    display: "flex",
                    gap: "4px",
                  }}
                >
                  <button
                    onClick={() => saveField(index, field)}
                    className="btn btn-sm"
                    style={{
                      background: purple,
                      color: "#fff",
                      borderRadius: 8,
                      padding: "2px 8px",
                      fontSize: "11px",
                    }}
                  >
                    Save
                  </button>
                  <button
                    onClick={() => handleCancel(index, field)}
                    className="btn btn-sm btn-light"
                    style={{
                      borderRadius: 8,
                      padding: "2px 8px",
                      fontSize: "11px",
                      border: "1px solid #ccc",
                    }}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <input
                disabled
                value={values[field]}
                className="form-control rounded-pill border-0"
                style={viewStyle}
              />
              <FontAwesomeIcon
                icon={faPen}
                onClick={() => toggleEdit(index, field)}
                style={{
                  position: "absolute",
                  right: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  cursor: "pointer",
                  color: purple,
                  zIndex: 2,
                  fontSize: 14,
                }}
              />
            </>
          )}
        </div>
      </div>
    );
  };

  const renderSelect = (index, label, field, options) => {
    const values = phoneList[index];
    const isEditing = editingList[index]?.[field];
    const viewStyle = {
      backgroundColor: pillBg,
      padding: "0.5rem 70px 0.5rem 0.5rem",
      cursor: "text",
      userSelect: "text",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    };
    const inputStyle = {
      backgroundColor: isEditing ? "#fff" : pillBg,
      border: isEditing ? `2px solid ${purple}` : "0",
      boxShadow: isEditing ? "0 0 5px rgba(46, 40, 104, 0.2)" : "none",
      padding: isEditing
        ? "0.5rem 110px 0.5rem 0.5rem"
        : "0.5rem 2rem 0.5rem 0.5rem",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      cursor: isCreate ? "pointer" : "text",
    };
    const selectedOption = options.find((o) => o.id === values[field]);
    const displayValue = selectedOption
      ? selectedOption.label || selectedOption.id
      : "";

    return (
      <div className="mb-3">
        <label className="form-label mb-1">{label}</label>
        <div style={{ position: "relative" }}>
          {isCreate || isEditing ? (
            <>
              <select
                value={values[field]}
                onChange={(e) => handleChange(index, field, e.target.value)}
                className="form-select rounded-pill"
                style={inputStyle}
              >
                <option value="">Select...</option>
                {options.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label || o.id}
                  </option>
                ))}
              </select>
              {isEditing && !isCreate && (
                <div
                  style={{
                    position: "absolute",
                    right: 6,
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 3,
                    display: "flex",
                    gap: "4px",
                  }}
                >
                  <button
                    onClick={() => saveField(index, field)}
                    className="btn btn-sm"
                    style={{
                      background: purple,
                      color: "#fff",
                      borderRadius: 8,
                      padding: "2px 8px",
                      fontSize: "11px",
                    }}
                  >
                    Save
                  </button>
                  <button
                    onClick={() => handleCancel(index, field)}
                    className="btn btn-sm btn-light"
                    style={{
                      borderRadius: 8,
                      padding: "2px 8px",
                      fontSize: "11px",
                      border: "1px solid #ccc",
                    }}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </>
          ) : (
            <>
              <div
                className="form-control rounded-pill border-0"
                style={viewStyle}
              >
                {displayValue || "Select..."}
              </div>
              <FontAwesomeIcon
                icon={faPen}
                onClick={() => toggleEdit(index, field)}
                style={{
                  position: "absolute",
                  right: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  cursor: "pointer",
                  color: purple,
                  zIndex: 2,
                  fontSize: 14,
                }}
              />
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="my-3" style={{ maxWidth: 490 }}>
      <div className="p-3">
        <div
          className="d-flex justify-content-between align-items-center mb-3"
          style={{ userSelect: "none" }}
        >
          <div className="d-flex align-items-center">
            <span
              style={{ fontSize: "30px", color: "#b2a9ff", marginRight: "6px" }}
            >
              ⋮⋮
            </span>
            <h5
              className="fw-bold m-0"
              style={{ color: purple, cursor: "pointer" }}
              onClick={() => setOpen((prev) => !prev)}
            >
              Phone Numbers
            </h5>
          </div>
          <div
            style={{
              width: "28px",
              height: "28px",
              borderRadius: "8px",
              backgroundColor: purple,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "transform 0.3s ease",
              transform: open ? "rotate(90deg)" : "rotate(0deg)",
            }}
            onClick={() => setOpen((prev) => !prev)}
          >
            <i className="bi bi-chevron-right" style={{ fontSize: "14px" }} />
          </div>
        </div>

        {open && (
          <>
            {phoneList.map((phone, index) => (
              <div
                key={index}
                className="mb-4 pb-3 border-bottom position-relative"
              >
                <div
                  style={{
                    position: "absolute",
                    right: 0,
                    top: -10,
                    zIndex: 10,
                  }}
                >
                  <FontAwesomeIcon
                    icon={faTrash}
                    onClick={() => handleRemoveField(index)}
                    style={{
                      color: purple,
                      cursor: "pointer",
                      fontSize: "16px",
                    }}
                  />
                </div>
                <div className="row g-2 mb-3">
                  <div className="col-12">
                    {renderInput(index, "Phone Number", "phone_number", "tel")}
                  </div>
                </div>
                <div className="row g-2">
                  <div className="col-6">
                    {renderSelect(
                      index,
                      "Phone Type",
                      "phone_type",
                      phone_types,
                    )}
                  </div>
                  <div className="col-6">
                    {renderSelect(
                      index,
                      "Best Day to Call",
                      "best_days_to_call",
                      best_days_to_call,
                    )}
                  </div>
                </div>
                <div className="row g-2">
                  <div className="col-6">
                    {renderInput(
                      index,
                      "Best Time to Call",
                      "best_time_to_call_start",
                      "time",
                    )}
                  </div>
                  <div className="col-6">
                    {renderInput(index, "End", "best_time_to_call_end", "time")}
                  </div>
                </div>
                <div className="form-check mb-2 mt-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={phone.preferred}
                    onChange={(e) =>
                      saveCheckbox(index, "preferred", e.target.checked)
                    }
                    id={`preferredPhone-${index}`}
                    style={{ cursor: "pointer" }}
                  />
                  <label
                    className="form-check-label"
                    htmlFor={`preferredPhone-${index}`}
                    style={{ cursor: "pointer" }}
                  >
                    Preferred
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={phone.sms_opt_in}
                    onChange={(e) =>
                      saveCheckbox(index, "sms_opt_in", e.target.checked)
                    }
                    id={`smsOptIn-${index}`}
                    style={{ cursor: "pointer" }}
                  />
                  <label
                    className="form-check-label"
                    htmlFor={`smsOptIn-${index}`}
                    style={{ cursor: "pointer" }}
                  >
                    Opt-in to SMS messages
                  </label>
                </div>
              </div>
            ))}
            <div className="text-center mt-3">
              <button
                onClick={handleAddField}
                className="btn btn-sm"
                style={{
                  border: `1px dashed ${purple}`,
                  color: purple,
                  borderRadius: "20px",
                  padding: "5px 20px",
                  backgroundColor: "transparent",
                }}
              >
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Add Another Phone Number
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PhoneNumbersSection;
